import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function TeamEmpowermentSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % 4);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const empowermentSteps = [
    {
      icon: "ri-graduation-cap-line",
      title: "AI Training Programs",
      description: "Comprehensive workshops to upskill your team",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: "ri-dashboard-line", 
      title: "Data Interpretation",
      description: "Learn to read and act on AI-driven insights",
      color: "from-cyan-500 to-green-500"
    },
    {
      icon: "ri-lightbulb-line",
      title: "AI-First Mindset",
      description: "Foster innovation and strategic thinking",
      color: "from-green-500 to-yellow-500"
    },
    {
      icon: "ri-rocket-line",
      title: "Long-term Success",
      description: "Sustainable growth through AI integration",
      color: "from-yellow-500 to-orange-500"
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                Empower Your Internal Team
              </span>
              <br />
              with AI Intelligence
            </h2>
            
            <h3 className="text-2xl font-semibold text-orange-400 mb-6">
              Cultivate an AI-Savvy Future Within Your Organization
            </h3>

            <div className="space-y-6 text-lg text-gray-300 leading-relaxed mb-8">
              <p>
                We believe that the power of AI is best realized when integrated into your team's capabilities. 
                Beyond implementing solutions, <strong className="text-orange-400">Hash Adz</strong> offers tailored 
                training programs and ongoing support to equip your internal marketing department with the knowledge 
                and skills to effectively utilize AI tools and interpret data-driven reports.
              </p>
              
              <p>
                We'll guide you in adopting an <strong className="text-orange-400">AI-first mindset</strong>, 
                fostering innovation and long-term success.
              </p>
            </div>

            <div className="mb-12 lg:mb-0">
              <Link to="/contact" className="group bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer inline-flex items-center gap-3">
                Start Team Training
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
              </Link>
            </div>
          </div>

          <div className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            {/* Mobile Grid Layout */}
            <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-6">
              {empowermentSteps.map((step, index) => (
                <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center mb-4`}>
                    <i className={`${step.icon} text-xl text-white`}></i>
                  </div>
                  <h4 className="text-white font-semibold mb-2">{step.title}</h4>
                  <p className="text-gray-400 text-sm">{step.description}</p>
                </div>
              ))}
            </div>

            {/* Desktop Orbital Layout */}
            <div className="hidden lg:block relative min-h-[500px]">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center animate-pulse z-20">
                <i className="ri-team-line text-4xl text-white"></i>
              </div>

              <div className="relative w-full h-full">
                {empowermentSteps.map((step, index) => {
                  const angle = (index * 90) - 90; 
                  const radius = 180;
                  const x = Math.cos((angle * Math.PI) / 180) * radius;
                  const y = Math.sin((angle * Math.PI) / 180) * radius;
                  
                  return (
                    <div
                      key={index}
                      className={`absolute w-24 h-24 transition-all duration-500`}
                      style={{
                        left: `calc(50% + ${x}px - 3rem)`,
                        top: `calc(50% + ${y}px - 3rem)`,
                        transform: currentStep === index ? 'scale(1.25)' : 'scale(1)',
                        zIndex: currentStep === index ? 30 : 10
                      }}
                    >
                      <div className={`w-full h-full rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center border-4 ${
                        currentStep === index ? 'border-white shadow-lg shadow-orange-500/50' : 'border-gray-700'
                      } transition-all duration-500`}>
                        <i className={`${step.icon} text-2xl text-white`}></i>
                      </div>
                      
                      {currentStep === index && (
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-4 bg-gray-900/95 backdrop-blur-md rounded-xl p-4 border border-orange-500/30 w-64 text-center z-40 animate-fade-in shadow-2xl">
                          <h4 className="text-white font-bold text-base mb-1">{step.title}</h4>
                          <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translate(-50%, 10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </section>
  );
}